import SafeSummaryTile from "../../../components/common/SafeSummaryTile";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import koLocale from "@fullcalendar/core/locales/ko";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import GroupsIcon from "@mui/icons-material/Groups";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import SchoolIcon from "@mui/icons-material/School";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { toast } from "react-toastify";

import { useProjectApi } from "../../../hooks/useProjectApi";
import { AutoPilotGuide } from "../../../components/common/AutoPilotGuide";
import {
  EmployeeBulkPicker,
  EmployeeSelect,
  getEmployeeDetailText,
  getEmployeeDisplayName,
} from "../../../components/common/EmployeeSelect";
import { EmptyState } from "../../../components/common/EmptyState";
import { SafePageHeader, ConfirmDialog } from "../../../components/common/HelperFunc";
import { axiosPrivate as axiosInstance } from "../../../../../api/axios";
import { buildMaterialForm, saveEducationMaterial } from "../../../utils/educationMaterial";
import { SummaryMetric } from "../../../components/common/SummaryMetric";

const EDU_TYPES = ["정기", "신규", "특별", "MSDS 교육", "관리감독자", "채용시", "작업변경시"];

const STATUS_OPTIONS = [
  { value: "pending", label: "대기중", color: "warning", icon: <CalendarMonthIcon /> },
  { value: "completed", label: "완료", color: "success", icon: <CheckCircleIcon /> },
  { value: "cancelled", label: "취소", color: "default", icon: <EventBusyIcon /> },
];

const ATTENDEE_STATUS_OPTIONS = [
  { value: "pending", label: "대기", color: "warning" },
  { value: "completed", label: "이수", color: "success" },
  { value: "absent", label: "불참", color: "error" },
];

const TYPE_COLORS = {
  정기: "#2563eb",
  신규: "#0891b2",
  특별: "#ea580c",
  "MSDS 교육": "#7c3aed",
  관리감독자: "#7c3aed",
  채용시: "#16a34a",
  작업변경시: "#dc2626",
};

const STATUS_COLORS = {
  pending: "#f59e0b",
  completed: "#16a34a",
  cancelled: "#64748b",
};

const EDUCATION_AUTOPILOT_STEPS = [
  {
    target: '[data-autopilot="education-summary"]',
    title: "교육 현황 확인",
    description:
      "상단 지표에서 전체 일정, 대기중, 완료, 취소, 대상자와 이수율을 먼저 확인합니다. 이 단계는 조회만 하며 데이터를 바꾸지 않습니다.",
  },
  {
    target: '[data-autopilot="education-calendar"]',
    title: "캘린더에서 일정 찾기",
    description:
      "달력에서 날짜별 교육을 확인합니다. 색상은 교육 유형을 구분하고, 일정을 누르면 상세 내용을 볼 수 있습니다.",
  },
  {
    target: '[data-autopilot="education-type-legend"]',
    title: "교육 유형 구분",
    description:
      "정기, 신규, 특별, 관리감독자, 채용시, 작업변경시 교육이 서로 다른 색상으로 표시됩니다.",
  },
  {
    target: '[data-autopilot="education-upcoming"]',
    title: "다가오는 일정 확인",
    description:
      "오른쪽 목록에서 가까운 교육 일정을 빠르게 확인합니다. 클릭하면 상세와 이수 상태를 확인할 수 있습니다.",
  },
  {
    target: '[data-autopilot="education-create"]',
    title: "일정 등록 위치",
    description:
      "웹, 태블릿, 모바일 화면에서 새 교육 일정을 등록하고 대상자 이수 상태, 교육자료 PDF, MSDS 관련 교육 이력을 함께 관리합니다.",
  },
];

const withAlpha = (hex, alpha) => {
  const normalized = hex.replace("#", "");
  const value = parseInt(normalized, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getStatusMeta = (status) =>
  STATUS_OPTIONS.find((item) => item.value === status) || STATUS_OPTIONS[0];

const getAttendeeStatusMeta = (status) =>
  ATTENDEE_STATUS_OPTIONS.find((item) => item.value === status) || ATTENDEE_STATUS_OPTIONS[0];

const getToday = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const toLocalDateString = (date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const getMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return {
    start: toLocalDateString(start),
    end: toLocalDateString(end),
  };
};

const makeEmptyForm = (date = getToday()) => ({
  edu_type: "정기",
  title: "",
  edu_date: `${date}T09:00`,
  duration_min: 60,
  location_id: "",
  target_desc: "",
  status: "pending",
  attendees: [],
});

const EMPTY_MATERIAL_FORM = {
  enabled: false,
  title: "",
  revision: 1,
  is_primary: 1,
};

const EMPTY_MSDS_TRAINING_FORM = {
  msds_id: "",
  machine_id: "",
  process_id: "",
  trainer_emp_id: "",
  note: "",
};

const buildEditForm = (schedule, fallbackDate = getToday()) => ({
  edu_type: schedule.edu_type || "정기",
  title: schedule.title || "",
  edu_date: schedule.edu_date_input || `${fallbackDate}T09:00`,
  duration_min: schedule.duration_min || 60,
  location_id: schedule.location_id || "",
  target_desc: schedule.target_desc || "",
  status: schedule.status || "pending",
  attendees: (schedule.attendees || []).map((item) => ({
    emp_id: Number(item.emp_id),
    status: item.status || "pending",
    completed_at: item.completed_at_input || "",
  })),
});

const createEvents = (schedules) =>
  schedules.map((schedule) => {
    const statusColor = STATUS_COLORS[schedule.status] || STATUS_COLORS.pending;
    const typeColor = TYPE_COLORS[schedule.edu_type] || "#2563eb";
    return {
      id: String(schedule.id),
      title: schedule.title,
      start: schedule.calendar_start,
      end: schedule.calendar_end,
      backgroundColor: schedule.status === "cancelled" ? "#f8fafc" : withAlpha(typeColor, 0.08),
      borderColor: schedule.status === "cancelled" ? statusColor : typeColor,
      textColor: "#111827",
      extendedProps: {
        ...schedule,
        statusColor,
        typeColor,
      },
    };
  });

const buildPayload = (form) => ({
  edu_type: form.edu_type,
  title: form.title,
  edu_date: form.edu_date,
  duration_min: form.duration_min,
  location_id: form.location_id,
  target_desc: form.target_desc,
  status: form.status,
  attendees: form.attendees,
});

const resolveMaterialUrl = (url) => !url ? "" : /^https?:\/\//i.test(url)
  ? url : `${axiosInstance.defaults.baseURL || ""}${url}`;

export function EducationMaterialLinks({ materials = [] }) {
  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      {(Array.isArray(materials) ? materials : []).filter((item) => item?.pdf_url).map((item) => (
        <Button key={item.material_id} component="a" href={resolveMaterialUrl(item.pdf_url)} target="_blank" rel="noopener noreferrer"
          startIcon={<PictureAsPdfIcon />} sx={{ justifyContent: "flex-start", textAlign: "left", overflowWrap: "anywhere" }}>
          {item.title || "교육자료 PDF"} (개정 {item.revision || 1})
        </Button>
      ))}
    </Stack>
  );
}

function SummaryCard(props) { return <SafeSummaryTile {...props} />; }

function EducationEventContent({ event }) {
  const schedule = event.extendedProps;
  const completedRate = Number(schedule.target_count)
    ? Math.round((Number(schedule.completed_count || 0) / Number(schedule.target_count || 0)) * 100)
    : 0;

  return (
    <Box sx={{ px: 0.5, py: 0.35, overflow: "hidden" }}>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Box
          sx={{
            width: 4,
            height: 18,
            borderRadius: 999,
            bgcolor: schedule.typeColor,
            flex: "0 0 auto",
          }}
        />
        <Typography variant="caption" sx={{ fontWeight: 900, color: "#111827" }} noWrap>
          {event.title}
        </Typography>
      </Stack>
      <Typography variant="caption" sx={{ color: schedule.typeColor, fontWeight: 800, pl: 1.35 }} noWrap>
        {schedule.edu_time} · {schedule.edu_type} · {completedRate}%
      </Typography>
    </Box>
  );
}

export function EducationScheduleDialog({
  open,
  mode,
  readOnly,
  fullScreen = false,
  form,
  detail,
  employees,
  isLoadingDetail,
  isSaving,
  onClose,
  onChange,
  onSubmit,
  onDelete,
  complianceOptions,
  materialForm,
  materialFile,
  msdsTrainingForm,
  onMaterialChange,
  onMaterialFileChange,
  onMsdsTrainingChange,
}) {
  const isEdit = mode === "edit";

  const attendeeMap = useMemo(
    () => new Map((form.attendees || []).map((item) => [Number(item.emp_id), item])),
    [form.attendees]
  );

  const targetCount = form.attendees?.length || 0;
  const completedCount = (form.attendees || []).filter((item) => item.status === "completed").length;
  const absentCount = (form.attendees || []).filter((item) => item.status === "absent").length;
  const completedRate = targetCount ? Math.round((completedCount / targetCount) * 100) : 0;
  const statusMeta = getStatusMeta(form.status);
  const isMsdsEducation = form.edu_type === "MSDS 교육";

  const setEmployeeTarget = (employee, checked) => {
    const empId = Number(employee.emp_id);
    if (checked) {
      onChange("attendees", [...(form.attendees || []), { emp_id: empId, status: "pending" }]);
      return;
    }
    onChange(
      "attendees",
      (form.attendees || []).filter((item) => Number(item.emp_id) !== empId)
    );
  };

  const setEmployeeTargets = (nextEmployees, checked) => {
    const currentAttendees = form.attendees || [];
    const currentMap = new Map(currentAttendees.map((item) => [Number(item.emp_id), item]));

    if (checked) {
      nextEmployees.forEach((employee) => {
        const empId = Number(employee.emp_id);
        if (!currentMap.has(empId)) currentMap.set(empId, { emp_id: empId, status: "pending" });
      });
    } else {
      nextEmployees.forEach((employee) => currentMap.delete(Number(employee.emp_id)));
    }

    onChange("attendees", Array.from(currentMap.values()));
  };

  const setAttendeeStatus = (employee, status) => {
    const empId = Number(employee.emp_id);
    onChange(
      "attendees",
      (form.attendees || []).map((item) =>
        Number(item.emp_id) === empId
          ? {
              ...item,
              status,
              completed_at: status === "completed" ? item.completed_at : "",
            }
          : item
      )
    );
  };

  const renderReadOnlyContent = () => (
    <Stack spacing={2}>
      <EducationMaterialLinks materials={detail?.materials} />
      <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider" }}>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Box>
              <Chip
                size="small"
                label={form.edu_type}
                sx={{ bgcolor: `${TYPE_COLORS[form.edu_type] || "#2563eb"}1a`, color: TYPE_COLORS[form.edu_type] || "#2563eb", fontWeight: 800 }}
              />
              <Typography variant="h6" sx={{ fontWeight: 900, mt: 1 }}>
                {form.title}
              </Typography>
            </Box>
            <Chip size="small" color={statusMeta.color} label={statusMeta.label} />
          </Stack>
          <Divider />
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                교육 일시
              </Typography>
              <Typography sx={{ fontWeight: 800 }}>{detail?.edu_day} {detail?.edu_time}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                교육 시간
              </Typography>
              <Typography sx={{ fontWeight: 800 }}>{form.duration_min || "-"}분</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                대상
              </Typography>
              <Typography sx={{ fontWeight: 800 }}>{form.target_desc || "-"}</Typography>
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider" }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography sx={{ fontWeight: 900 }}>이수 현황</Typography>
          <Typography sx={{ fontWeight: 900 }}>{completedRate}%</Typography>
        </Stack>
        <LinearProgress variant="determinate" value={completedRate} sx={{ height: 10, borderRadius: 999 }} />
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          <Chip size="small" icon={<GroupsIcon />} label={`대상 ${targetCount}명`} />
          <Chip size="small" color="success" label={`이수 ${completedCount}명`} />
          <Chip size="small" color="error" label={`불참 ${absentCount}명`} />
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider" }}>
        <Typography sx={{ fontWeight: 900, mb: 1 }}>대상자</Typography>
        <List disablePadding>
          {(detail?.attendees || []).map((attendee) => {
            const attendeeStatus = getAttendeeStatusMeta(attendee.status);
            return (
              <ListItem key={attendee.emp_id} divider sx={{ px: 0 }}>
                <ListItemText
                  primary={getEmployeeDisplayName(attendee)}
                  secondary={getEmployeeDetailText(attendee)}
                />
                <Chip size="small" color={attendeeStatus.color} label={attendeeStatus.label} />
              </ListItem>
            );
          })}
          {!detail?.attendees?.length ? (
            <ListItem sx={{ px: 0 }}>
              <ListItemText primary="등록된 대상자가 없습니다." />
            </ListItem>
          ) : null}
        </List>
      </Paper>
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen || readOnly}
      PaperProps={{
        sx: fullScreen || readOnly
          ? undefined
          : {
              height: { xs: "calc(100dvh - 32px)", md: 760 },
              maxHeight: "calc(100dvh - 32px)",
            },
      }}
    >
      <DialogTitle sx={{ py: 2, pr: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {readOnly ? "교육 일정 상세" : isEdit ? "교육 일정 수정" : "교육 일정 등록"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {readOnly ? "교육 일정과 대상자 이수 상태를 확인합니다." : "교육 일정과 대상자 이수 상태를 함께 관리합니다."}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers
        sx={
          readOnly
            ? { bgcolor: "#f7f8fb" }
            : {
                bgcolor: "#f7f8fb",
                overflow: { xs: "auto", md: "hidden" },
                display: "flex",
                flexDirection: "column",
              }
        }
      >
        {isLoadingDetail ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 360 }}>
            <CircularProgress />
          </Stack>
        ) : readOnly ? (
          renderReadOnlyContent()
        ) : (
          <Grid container spacing={2.25} sx={{ flex: 1, minHeight: 0 }}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider" }}>
                <Typography sx={{ fontWeight: 900, mb: 2 }}>교육 정보</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={5}>
                    <FormControl fullWidth>
                      <InputLabel>교육 유형</InputLabel>
                      <Select
                        label="교육 유형"
                        value={form.edu_type}
                        onChange={(event) => onChange("edu_type", event.target.value)}
                      >
                        {EDU_TYPES.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <FormControl fullWidth>
                      <InputLabel>상태</InputLabel>
                      <Select
                        label="상태"
                        value={form.status}
                        onChange={(event) => onChange("status", event.target.value)}
                      >
                        {STATUS_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="교육명"
                      value={form.title}
                      onChange={(event) => onChange("title", event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <TextField
                      fullWidth
                      required
                      type="datetime-local"
                      label="교육 일자"
                      value={form.edu_date}
                      onChange={(event) => onChange("edu_date", event.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      type="number"
                      label="교육 시간(분)"
                      inputProps={{ min: 1 }}
                      value={form.duration_min}
                      onChange={(event) => onChange("duration_min", event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="대상 설명"
                      placeholder="예: 조립 C라인 전체"
                      value={form.target_desc}
                      onChange={(event) => onChange("target_desc", event.target.value)}
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Paper
                elevation={0}
                sx={{ p: 2, mt: 2, border: "1px solid", borderColor: "divider" }}
              >
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1.25}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar sx={{ width: 34, height: 34, bgcolor: "#ede9fe", color: "#6d5dfc" }}>
                        <PictureAsPdfIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontWeight: 900 }}>교육자료 PDF</Typography>
                        <Typography variant="caption" color="text.secondary">
                          교육 일정과 함께 교안, 자료집, 안내문을 보관합니다.
                        </Typography>
                      </Box>
                    </Stack>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={materialForm.enabled}
                          disabled={Boolean(materialForm.material_id)}
                          onChange={(event) => onMaterialChange("enabled", event.target.checked)}
                        />
                      }
                      label="자료 첨부"
                      sx={{ mr: 0 }}
                    />
                  </Stack>

                  <EducationMaterialLinks materials={detail?.materials} />

                  {materialForm.enabled ? (
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          fullWidth
                          label="자료명"
                          placeholder="ex) 7월 정기 안전보건교육 교안"
                          value={materialForm.title}
                          onChange={(event) => onMaterialChange("title", event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          type="number"
                          label="개정"
                          inputProps={{ min: 1 }}
                          value={materialForm.revision}
                          onChange={(event) => onMaterialChange("revision", event.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1.25}
                          alignItems={{ xs: "stretch", sm: "center" }}
                          justifyContent="space-between"
                          sx={{
                            p: 1.5,
                            border: "1px dashed",
                            borderColor: materialFile ? "primary.main" : "divider",
                            borderRadius: 1.5,
                            bgcolor: materialFile ? "#f5f3ff" : "#fff",
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {materialFile ? materialFile.name : materialForm.pdf_url ? "기존 PDF 유지" : "교육자료 PDF를 선택하세요."}
                          </Typography>
                          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
                            {materialForm.pdf_url ? "PDF 교체" : "PDF 선택"}
                            <input
                              hidden
                              type="file"
                              accept="application/pdf"
                              onChange={(event) =>
                                onMaterialFileChange(event.target.files?.[0] || null)
                              }
                            />
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  ) : null}
                </Stack>
              </Paper>

              {isMsdsEducation ? (
              <Paper
                elevation={0}
                sx={{ p: 2, mt: 2, border: "1px solid", borderColor: "#ddd6fe", bgcolor: "#fbfaff" }}
              >
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1.25}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar sx={{ width: 34, height: 34, bgcolor: "#e0f2fe", color: "#0284c7" }}>
                        <MenuBookIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontWeight: 900 }}>MSDS 관련 교육</Typography>
                        <Typography variant="caption" color="text.secondary">
                          교육 유형이 MSDS 교육이면 이수 이력에도 함께 남깁니다.
                        </Typography>
                      </Box>
                    </Stack>
                    <Chip size="small" color="primary" label="교육 유형 연동" sx={{ fontWeight: 900 }} />
                  </Stack>

                    <Grid container spacing={1.5}>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>대상 MSDS</InputLabel>
                          <Select
                            label="대상 MSDS"
                            value={msdsTrainingForm.msds_id || ""}
                            onChange={(event) =>
                              onMsdsTrainingChange("msds_id", event.target.value)
                            }
                          >
                            {(complianceOptions.msds || []).map((msds) => (
                              <MenuItem key={msds.id} value={msds.id}>
                                {msds.product_name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>관련 설비·구역</InputLabel>
                          <Select
                            label="관련 설비·구역"
                            value={msdsTrainingForm.machine_id || ""}
                            onChange={(event) =>
                              onMsdsTrainingChange("machine_id", event.target.value)
                            }
                          >
                            <MenuItem value="">선택 안함</MenuItem>
                            {(complianceOptions.machines || []).map((machine) => (
                              <MenuItem key={machine.machine_id} value={machine.machine_id}>
                                {machine.display_name || machine.machine_name || `설비·구역 ${machine.machine_id}`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>관련 평가 대상</InputLabel>
                          <Select
                            label="관련 평가 대상"
                            value={msdsTrainingForm.process_id || ""}
                            onChange={(event) =>
                              onMsdsTrainingChange("process_id", event.target.value)
                            }
                          >
                            <MenuItem value="">선택 안함</MenuItem>
                            {(complianceOptions.processes || []).map((process) => (
                              <MenuItem key={process.process_id} value={process.process_id}>
                                {process.process_name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <EmployeeSelect
                          employees={employees}
                          value={msdsTrainingForm.trainer_emp_id || ""}
                          onChange={(value) => onMsdsTrainingChange("trainer_emp_id", value)}
                          label="강사"
                          helperText="팀을 먼저 선택하면 강사를 빠르게 찾을 수 있습니다."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="비고"
                          value={msdsTrainingForm.note}
                          onChange={(event) => onMsdsTrainingChange("note", event.target.value)}
                        />
                      </Grid>
                    </Grid>
                </Stack>
              </Paper>
              ) : null}

              <Paper
                elevation={0}
                sx={{ p: 2, mt: 2, border: "1px solid", borderColor: "divider" }}
              >
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 900 }}>이수율</Typography>
                  <Typography sx={{ fontWeight: 900 }}>{completedRate}%</Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={completedRate}
                  sx={{ height: 10, borderRadius: 999 }}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                  <Chip size="small" icon={<GroupsIcon />} label={`대상 ${targetCount}명`} />
                  <Chip size="small" color="success" label={`이수 ${completedCount}명`} />
                  <Chip size="small" color="error" label={`불참 ${absentCount}명`} />
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <EmployeeBulkPicker
                employees={employees}
                selectedIds={(form.attendees || []).map((item) => item.emp_id)}
                title="교육 대상자"
                selectedLabel="대상"
                helperText="팀 전체를 한 번에 대상자로 넣거나, 이름·사번으로 검색해서 일부만 선택할 수 있습니다."
                listHeight={{ xs: "calc(100dvh - 390px)", md: 460 }}
                fixedListHeight
                onSelectEmployee={setEmployeeTarget}
                onSelectEmployees={(nextEmployees) => setEmployeeTargets(nextEmployees, true)}
                onDeselectEmployees={(nextEmployees) => setEmployeeTargets(nextEmployees, false)}
                renderSecondaryAction={(employee, selected) => {
                  const attendee = attendeeMap.get(Number(employee.emp_id));
                  return (
                    <FormControl size="small" sx={{ minWidth: 104 }}>
                      <Select
                        value={attendee?.status || "pending"}
                        disabled={!selected}
                        onChange={(event) => setAttendeeStatus(employee, event.target.value)}
                      >
                        {ATTENDEE_STATUS_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                }}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {!readOnly && isEdit ? (
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={onDelete}
            disabled={isSaving || isLoadingDetail}
            sx={{ mr: "auto" }}
          >
            삭제
          </Button>
        ) : null}
        <Button onClick={onClose} color="inherit">
          {readOnly ? "닫기" : "취소"}
        </Button>
        {!readOnly ? (
          <Button
            variant="contained"
            startIcon={<SchoolIcon />}
            disabled={isSaving || isLoadingDetail}
            onClick={onSubmit}
          >
            {isEdit ? "수정 완료" : "등록 완료"}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

function MobileTodayList({ schedules, selectedDay, onSelectDay, onOpen }) {
  const daySchedules = schedules.filter((schedule) => schedule.edu_day === selectedDay);

  return (
    <Stack spacing={1.25}>
      <TextField
        fullWidth
        type="date"
        label="일자"
        value={selectedDay}
        onChange={(event) => onSelectDay(event.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      {daySchedules.map((schedule) => {
        const status = getStatusMeta(schedule.status);
        const completedRate = Number(schedule.target_count)
          ? Math.round((Number(schedule.completed_count || 0) / Number(schedule.target_count || 0)) * 100)
          : 0;
        return (
          <Card
            key={schedule.id}
            variant="outlined"
            sx={{ borderRadius: 2, cursor: "pointer" }}
            onClick={() => onOpen(schedule)}
          >
            <CardContent>
              <Stack spacing={1.25}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Chip
                      size="small"
                      label={schedule.edu_type}
                      sx={{
                        mb: 1,
                        color: TYPE_COLORS[schedule.edu_type] || "#2563eb",
                        bgcolor: `${TYPE_COLORS[schedule.edu_type] || "#2563eb"}1a`,
                        fontWeight: 800,
                      }}
                    />
                    <Typography sx={{ fontWeight: 900 }} noWrap>
                      {schedule.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {schedule.edu_time} · {schedule.duration_min || 60}분
                    </Typography>
                  </Box>
                  <Chip size="small" color={status.color} label={status.label} />
                </Stack>
                <LinearProgress variant="determinate" value={completedRate} sx={{ height: 8, borderRadius: 999 }} />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" icon={<GroupsIcon />} label={`대상 ${schedule.target_count || 0}명`} />
                  <Chip size="small" color="success" label={`이수 ${schedule.completed_count || 0}명`} />
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        );
      })}
      {!daySchedules.length ? (
        <EmptyState
          compact
          icon={<SchoolIcon />}
          title="선택한 날짜의 교육 일정이 없습니다"
          description="캘린더에서 날짜를 선택하거나 일정 등록 버튼으로 교육 일정을 등록하고 이수 상태를 관리합니다."
        />
      ) : null}
    </Stack>
  );
}

export function EducationScheduleManagement() {
  const { group_code, project_code } = useParams();
  const [searchParams] = useSearchParams();
  const focusEducationId = searchParams.get("education_id") || "";
  const requestedDate = searchParams.get("date") || "";
  const api = useProjectApi(group_code, project_code);
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const isPhone = useMediaQuery(theme.breakpoints.down("md"));
  const showSidePanel = !isPhone;
  const isReadOnlyMode = false;
  const calendarRef = useRef(null);
  const focusHandledRef = useRef("");

  const initialDate = /^\d{4}-\d{2}-\d{2}$/.test(requestedDate.slice(0, 10))
    ? requestedDate.slice(0, 10)
    : getToday();
  const [range, setRange] = useState(() => {
    const date = new Date(`${initialDate}T00:00:00`);
    return {
      start: toLocalDateString(new Date(date.getFullYear(), date.getMonth(), 1)),
      end: toLocalDateString(new Date(date.getFullYear(), date.getMonth() + 1, 1)),
    };
  });
  const [selectedDay, setSelectedDay] = useState(initialDate);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(makeEmptyForm());
  const [materialForm, setMaterialForm] = useState(EMPTY_MATERIAL_FORM);
  const [materialFile, setMaterialFile] = useState(null);
  const [msdsTrainingForm, setMsdsTrainingForm] = useState(EMPTY_MSDS_TRAINING_FORM);
  const [detail, setDetail] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [autopilotOpen, setAutopilotOpen] = useState(false);
  const [autopilotStepIndex, setAutopilotStepIndex] = useState(0);

  const optionsQuery = useQuery({
    queryKey: ["risk-edu-options", group_code, project_code],
    queryFn: async () => {
      const response = await api.get("/edu/options");
      return response.data || { employees: [] };
    },
    enabled: Boolean(group_code && project_code),
    placeholderData: { employees: [] },
  });

  const complianceOptionsQuery = useQuery({
    queryKey: ["risk-edu-compliance-options", group_code, project_code],
    queryFn: async () => {
      const response = await api.get("/compliance/options");
      return response.data || { msds: [], machines: [], processes: [] };
    },
    enabled: Boolean(group_code && project_code),
    placeholderData: { msds: [], machines: [], processes: [] },
  });

  const schedulesQuery = useQuery({
    queryKey: ["risk-edu-schedules", group_code, project_code, range.start, range.end],
    queryFn: async () => {
      const response = await api.get("/edu/schedules", {
        params: {
          start: range.start,
          end: range.end,
        },
      });
      return response.data || [];
    },
    enabled: Boolean(group_code && project_code && range.start && range.end),
    placeholderData: [],
  });

  const employees = useMemo(() => optionsQuery.data?.employees || [], [optionsQuery.data]);
  const schedules = useMemo(() => schedulesQuery.data || [], [schedulesQuery.data]);
  const filteredSchedules = useMemo(() => {
    if (statusFilter === "all") return schedules;
    return schedules.filter((schedule) => schedule.status === statusFilter);
  }, [schedules, statusFilter]);
  const events = useMemo(() => createEvents(filteredSchedules), [filteredSchedules]);

  const invalidateSchedules = () => {
    queryClient.invalidateQueries({
      queryKey: ["risk-edu-schedules", group_code, project_code, range.start, range.end],
    });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload(form);
      let response;
      if (dialogMode === "edit") {
        response = await api.put(`/edu/schedules/${editingId}`, payload);
      } else {
        response = await api.post("/edu/schedules", payload);
      }

      const scheduleId = response?.data?.data?.id || editingId;
      if (!scheduleId) throw new Error("저장된 교육 일정 ID를 확인할 수 없습니다.");
      // Keep the saved schedule on attachment failure so retry does not duplicate it.
      setEditingId(scheduleId);
      setDialogMode("edit");

      if (materialForm.enabled) {
        try {
          const saved = await saveEducationMaterial(api, scheduleId, materialForm, materialFile, `${form.title || "교육"} 자료`);
          const value = saved?.data?.data;
          if (value?.material_id) setMaterialForm((prev) => ({ ...prev, ...value }));
        } catch (error) {
          throw new Error("교육 일정은 저장되었지만 PDF 저장에 실패했습니다. 파일을 확인한 뒤 다시 저장해주세요.");
        }
      }

      if (form.edu_type === "MSDS 교육") {
        await api.post("/compliance/msds-trainings", {
          msds_id: msdsTrainingForm.msds_id,
          machine_id: msdsTrainingForm.machine_id || null,
          process_id: msdsTrainingForm.process_id || null,
          title: form.title,
          trainer_emp_id: msdsTrainingForm.trainer_emp_id || null,
          training_at: form.edu_date,
          duration_min: form.duration_min,
          target_desc: form.target_desc,
          note: msdsTrainingForm.note,
          attendee_ids: (form.attendees || []).map((attendee) => attendee.emp_id),
          attendee_status: 1,
        });
      }

      return response;
    },
    onSuccess: () => {
      const withExtra =
        materialForm.enabled || form.edu_type === "MSDS 교육"
          ? " 교육자료와 관련 이력도 함께 저장되었습니다."
          : "";
      toast.success(
        `${dialogMode === "edit" ? "교육 일정이 저장되었습니다." : "교육 일정이 등록되었습니다."}${withExtra}`
      );
      closeDialog();
      invalidateSchedules();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error.message || "교육 일정 저장 중 오류가 발생했습니다.");
      invalidateSchedules();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (ids) => api.delete("/edu/schedules", { data: { ids } }),
    onSuccess: () => {
      toast.success("교육 일정이 삭제되었습니다.");
      setDeleteConfirmOpen(false);
      closeDialog();
      invalidateSchedules();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "교육 일정 삭제 중 오류가 발생했습니다.");
    },
  });

  const summary = useMemo(() => {
    const totalTarget = schedules.reduce((sum, item) => sum + Number(item.target_count || 0), 0);
    const totalCompleted = schedules.reduce((sum, item) => sum + Number(item.completed_count || 0), 0);
    const pending = schedules.filter((item) => item.status === "pending").length;
    const completed = schedules.filter((item) => item.status === "completed").length;
    const cancelled = schedules.filter((item) => item.status === "cancelled").length;
    const rate = totalTarget ? Math.round((totalCompleted / totalTarget) * 100) : 0;
    return { total: schedules.length, pending, completed, cancelled, totalTarget, totalCompleted, rate };
  }, [schedules]);

  const openCreateDialog = (dateInput = `${selectedDay}T09:00`) => {
    const nextForm = makeEmptyForm(dateInput.slice(0, 10));
    setDialogMode("create");
    setEditingId(null);
    setDetail(null);
    setForm({ ...nextForm, edu_date: dateInput });
    setMaterialForm(EMPTY_MATERIAL_FORM);
    setMaterialFile(null);
    setMsdsTrainingForm(EMPTY_MSDS_TRAINING_FORM);
    setDialogOpen(true);
  };

  const openScheduleDialog = async (schedule, readOnlyMode = isReadOnlyMode) => {
    setDialogMode(readOnlyMode ? "read" : "edit");
    setEditingId(schedule.id);
    setDialogOpen(true);
    setIsLoadingDetail(true);
    setDetail(null);
    setMaterialForm(EMPTY_MATERIAL_FORM);
    setMaterialFile(null);

    try {
      const response = await api.get(`/edu/schedules/${schedule.id}`);
      setDetail(response.data);
      setForm(buildEditForm(response.data, schedule.edu_day));
      setMaterialForm(buildMaterialForm(response.data?.materials, `${response.data?.title || "교육"} 자료`));
      setMaterialFile(null);
      setMsdsTrainingForm(EMPTY_MSDS_TRAINING_FORM);
    } catch (error) {
      toast.error(error?.response?.data?.message || "교육 일정 상세 조회 중 오류가 발생했습니다.");
      closeDialog();
    } finally {
      setIsLoadingDetail(false);
    }
  };

  useEffect(() => {
    if (!focusEducationId || schedulesQuery.isLoading || schedulesQuery.isPlaceholderData || focusHandledRef.current === focusEducationId) return;
    const target = schedules.find((schedule) => String(schedule?.id) === String(focusEducationId));
    focusHandledRef.current = focusEducationId;
    if (!target) {
      toast.info("연결된 교육 일정을 찾을 수 없습니다.");
      return;
    }
    const targetDay = String(target.edu_day || target.edu_date || requestedDate).slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(targetDay)) setSelectedDay(targetDay);
    openScheduleDialog(target, true);
  }, [focusEducationId, requestedDate, schedules, schedulesQuery.isLoading, schedulesQuery.isPlaceholderData]);

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogMode("create");
    setEditingId(null);
    setDetail(null);
    setIsLoadingDetail(false);
    setForm(makeEmptyForm(selectedDay));
    setMaterialForm(EMPTY_MATERIAL_FORM);
    setMaterialFile(null);
    setMsdsTrainingForm(EMPTY_MSDS_TRAINING_FORM);
  };

  const handleFormChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleMaterialChange = (key, value) => {
    setMaterialForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleMsdsTrainingChange = (key, value) => {
    setMsdsTrainingForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (!String(form.title || "").trim()) {
      toast.warning("교육명을 입력해주세요.");
      return;
    }
    if (!form.edu_date) {
      toast.warning("교육 일자를 입력해주세요.");
      return;
    }
    if (materialForm.enabled && !materialFile && !materialForm.pdf_url) {
      toast.warning("교육자료 PDF를 선택해주세요.");
      return;
    }
    if (form.edu_type === "MSDS 교육" && !msdsTrainingForm.msds_id) {
      toast.warning("MSDS 관련 교육으로 저장하려면 대상 MSDS를 선택해주세요.");
      return;
    }
    saveMutation.mutate();
  };

  const handleDelete = () => {
    if (!editingId) return;
    setDeleteConfirmOpen(true);
  };

  const handleDatesSet = (info) => {
    const start = info.startStr.slice(0, 10);
    const end = info.endStr.slice(0, 10);
    setRange((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  };

  const handleDateClick = (info) => {
    setSelectedDay(info.dateStr);
    if (!isReadOnlyMode) {
      openCreateDialog(`${info.dateStr}T09:00`);
    }
  };

  const handleEventClick = (info) => {
    const schedule = info.event.extendedProps;
    openScheduleDialog(schedule, isReadOnlyMode);
  };

  return (
    <Box className="safe-page-root"
      sx={{
        p: { xs: 1.25, sm: 1.5, lg: 3 },
        pb: { xs: "max(16px, env(safe-area-inset-bottom))", lg: 3 },
        height: "100%",
        minHeight: 0,
        overflow: "auto",
        overflowX: "hidden",
        bgcolor: "#f7f8fb",
      }}
    >
      <Stack className="safe-page-stack"
        spacing={{ xs: 1.5, lg: 2.5 }}
        sx={{ minHeight: "100%", width: "100%", maxWidth: { xs: "100%", xl: 1540 }, mx: "auto" }}
      >
        <SafePageHeader title="근로자 안전보건교육 관리" actions={<Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Tooltip title="오토파일럿">
                <IconButton
                  color="primary"
                  onClick={() => {
                    setAutopilotStepIndex(0);
                    setAutopilotOpen(true);
                  }}
                  sx={{
                    alignSelf: { xs: "flex-start", sm: "center" },
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <PlayCircleOutlineIcon />
                </IconButton>
              </Tooltip>
              {!isReadOnlyMode ? (
                <Button data-autopilot="education-create" variant="contained" startIcon={<AddIcon />} onClick={() => openCreateDialog()}>
                  일정 등록
                </Button>
              ) : null}
            </Stack>}></SafePageHeader>

        <Grid className="safe-mobile-three-summary" container spacing={1.5} data-autopilot="education-summary">
          <Grid item xs={4} md={2}>
            <SummaryCard
              icon={<MenuBookIcon />}
              label="전체 일정"
              value={`${summary.total}건`}
              active={statusFilter === "all"}
              onClick={() => setStatusFilter("all")}
            />
          </Grid>
          <Grid item xs={4} md={2}>
            <SummaryCard
              icon={<CalendarMonthIcon />}
              label="대기중"
              value={`${summary.pending}건`}
              color="#f59e0b"
              active={statusFilter === "pending"}
              onClick={() => setStatusFilter("pending")}
            />
          </Grid>
          <Grid item xs={4} md={2}>
            <SummaryCard
              icon={<CheckCircleIcon />}
              label="완료"
              value={`${summary.completed}건`}
              color="#16a34a"
              active={statusFilter === "completed"}
              onClick={() => setStatusFilter("completed")}
            />
          </Grid>
          <Grid item xs={4} md={2}>
            <SummaryCard
              icon={<EventBusyIcon />}
              label="취소"
              value={`${summary.cancelled}건`}
              color="#64748b"
              active={statusFilter === "cancelled"}
              onClick={() => setStatusFilter("cancelled")}
            />
          </Grid>
          <Grid item xs={4} md={2}>
            <SummaryCard icon={<GroupsIcon />} label="대상자" value={`${summary.totalTarget}명`} />
          </Grid>
          <Grid item xs={4} md={2}>
            <SummaryCard
              icon={<SchoolIcon />}
              label="이수율"
              value={`${summary.rate}%`}
              color="#16a34a"
            />
          </Grid>
        </Grid>

        {schedulesQuery.isError ? (
          <Alert severity="error">교육 일정 데이터를 불러오는 중 오류가 발생했습니다.</Alert>
        ) : null}

        <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
          <Grid item xs={12} md={showSidePanel ? 8 : 12} lg={9.5}>
            <Paper
              data-autopilot="education-calendar"
              elevation={0}
              sx={{
                p: { xs: 1, md: 2 },
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "#fff",
                height: {
                  xs: "auto",
                  sm: "auto",
                  md: isMobile ? "auto" : "calc(100dvh - 260px)",
                  lg: "calc(100vh - 268px)",
                },
                minHeight: { xs: 0, md: isMobile ? 0 : 620, lg: 640 },
                position: "relative",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                "& .fc": {
                  fontFamily: "inherit",
                  flex: 1,
                  minHeight: 0,
                },
                "& .fc-toolbar-title": {
                  fontSize: { xs: "1.05rem", md: "1.35rem" },
                  fontWeight: 900,
                },
                "& .fc-header-toolbar": {
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: { xs: 1, sm: 0 },
                  alignItems: "center",
                },
                "& .fc-toolbar-chunk": {
                  display: "flex",
                  justifyContent: { xs: "center", sm: "initial" },
                  flexWrap: "wrap",
                  gap: 0.5,
                },
                "& .fc-button": {
                  height: { xs: "44px", lg: "34px" },
                  borderRadius: "9px !important",
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: "0.82rem",
                  padding: "0 12px !important",
                  boxShadow: "none !important",
                },
                "& .fc-prev-button, & .fc-next-button": {
                  minWidth: "34px",
                  width: "34px",
                  padding: "0 !important",
                },
                "& .fc-button-primary": {
                  bgcolor: "#fff !important",
                  borderColor: "#dbe3ee !important",
                  color: "#334155 !important",
                },
                "& .fc-button-primary:not(:disabled):hover": {
                  bgcolor: "#f8fafc !important",
                  borderColor: "#b9c4d4 !important",
                  color: "#0f172a !important",
                },
                "& .fc-button-primary:not(:disabled).fc-button-active": {
                  bgcolor: "#0f172a !important",
                  borderColor: "#0f172a !important",
                  color: "#fff !important",
                },
                "& .fc-button-primary:disabled": {
                  bgcolor: "#eef2f7 !important",
                  borderColor: "#e2e8f0 !important",
                  color: "#94a3b8 !important",
                  opacity: "1 !important",
                },
                "& .fc-daygrid-event": {
                  borderRadius: "7px",
                  borderWidth: "1px",
                  borderStyle: "solid",
                  overflow: "hidden",
                  boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
                },
                "& .fc-event-main": {
                  color: "#111827",
                },
                "& .fc-daygrid-day-number": {
                  color: "text.primary",
                  fontWeight: 700,
                },
              }}
            >
              {schedulesQuery.isFetching ? (
                <LinearProgress sx={{ position: "absolute", left: 0, right: 0, top: 0, zIndex: 2 }} />
              ) : null}
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                locale={koLocale}
                initialDate={initialDate}
                initialView={isMobile ? "dayGridMonth" : "dayGridMonth"}
                headerToolbar={
                  isMobile
                    ? {
                        left: "title",
                        center: "",
                        right: "prev,next today",
                      }
                    : {
                        left: "prev,next today",
                        center: "title",
                        right: "dayGridMonth,timeGridWeek,timeGridDay",
                      }
                }
                buttonText={{
                  today: "오늘",
                  month: "월",
                  week: "주",
                  day: "일",
                }}
                events={events}
                datesSet={handleDatesSet}
                dateClick={handleDateClick}
                eventClick={handleEventClick}
                eventContent={(eventInfo) => isPhone
                  ? <Box title={eventInfo.event.title} sx={{ fontSize: 11, p: 0.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{eventInfo.event.title}</Box>
                  : <EducationEventContent event={eventInfo.event} />}
                selectable={!isReadOnlyMode}
                selectMirror={!isReadOnlyMode}
                dayMaxEvents={isMobile ? 2 : 4}
                height={isMobile ? "auto" : "100%"}
                eventTimeFormat={{
                  hour: "2-digit",
                  minute: "2-digit",
                  meridiem: false,
                }}
              />
            </Paper>
          </Grid>

          {showSidePanel ? (
            <Grid item xs={12} md={4} lg={2.5}>
              <Paper
                data-autopilot="education-type-legend"
                elevation={0}
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "#fff",
                  height: "100%",
                  minHeight: { md: isMobile ? 560 : 640 },
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Typography sx={{ fontWeight: 900, mb: 1 }}>교육 유형</Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {EDU_TYPES.map((type) => (
                    <Stack
                      key={type}
                      direction="row"
                      alignItems="center"
                      spacing={0.6}
                      sx={{
                        px: 0.9,
                        py: 0.45,
                        border: "1px solid",
                        borderColor: withAlpha(TYPE_COLORS[type], 0.22),
                        borderRadius: 999,
                        bgcolor: withAlpha(TYPE_COLORS[type], 0.08),
                      }}
                    >
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          bgcolor: TYPE_COLORS[type],
                          flex: "0 0 auto",
                        }}
                      />
                      <Typography variant="caption" sx={{ color: TYPE_COLORS[type], fontWeight: 900 }}>
                        {type}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Typography sx={{ fontWeight: 900, mb: 1.5 }}>다가오는 일정</Typography>
                <Stack data-autopilot="education-upcoming" spacing={1} sx={{ flex: 1, minHeight: 0, overflow: "auto", pr: 0.5 }}>
                  {filteredSchedules.slice(0, 8).map((schedule) => {
                    const status = getStatusMeta(schedule.status);
                    const typeColor = TYPE_COLORS[schedule.edu_type] || "#2563eb";
                    return (
                      <Paper
                        key={schedule.id}
                        variant="outlined"
                        sx={{
                          p: 1.25,
                          cursor: "pointer",
                          borderColor: "divider",
                          borderLeftWidth: 4,
                          borderLeftStyle: "solid",
                          borderLeftColor: typeColor,
                          bgcolor: withAlpha(typeColor, 0.04),
                          transition: "border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
                          "&:hover": {
                            borderColor: typeColor,
                            boxShadow: "0 8px 22px rgba(15, 23, 42, 0.08)",
                            transform: "translateY(-1px)",
                          },
                        }}
                        onClick={() => openScheduleDialog(schedule, isReadOnlyMode)}
                      >
                        <Stack spacing={0.75}>
                          <Stack direction="row" justifyContent="space-between" spacing={1}>
                            <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                              {schedule.title}
                            </Typography>
                            <Chip size="small" color={status.color} label={status.label} />
                          </Stack>
                          <Chip
                            size="small"
                            label={schedule.edu_type}
                            sx={{
                              alignSelf: "flex-start",
                              height: 22,
                              bgcolor: withAlpha(typeColor, 0.12),
                              color: typeColor,
                              fontWeight: 900,
                              border: `1px solid ${withAlpha(typeColor, 0.22)}`,
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {schedule.edu_day} {schedule.edu_time}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            대상 {schedule.target_count || 0}명 · 이수 {schedule.completed_count || 0}명
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                  })}
                  {!filteredSchedules.length ? (
                    <EmptyState
                      compact
                      icon={<SchoolIcon />}
                      title="표시할 교육 일정이 없습니다"
                      description="일정 등록 버튼으로 교육명, 대상자, 이수 상태를 등록하면 캘린더와 목록에 함께 표시됩니다."
                      actionLabel="일정 등록"
                      actionIcon={<AddIcon />}
                      onAction={() => openCreateDialog()}
                    />
                  ) : null}
                </Stack>
              </Paper>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <MobileTodayList
                schedules={filteredSchedules}
                selectedDay={selectedDay}
                onSelectDay={setSelectedDay}
                onOpen={(schedule) => openScheduleDialog(schedule, isReadOnlyMode)}
              />
            </Grid>
          )}
        </Grid>

      </Stack>

      <EducationScheduleDialog
        open={dialogOpen}
        mode={dialogMode}
        readOnly={isReadOnlyMode || dialogMode === "read"}
        fullScreen={isPhone}
        form={form}
        detail={detail}
        employees={employees}
        isLoadingDetail={isLoadingDetail}
        isSaving={saveMutation.isPending || deleteMutation.isPending}
        onClose={closeDialog}
        onChange={handleFormChange}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        complianceOptions={complianceOptionsQuery.data || { msds: [], machines: [], processes: [] }}
        materialForm={materialForm}
        materialFile={materialFile}
        msdsTrainingForm={msdsTrainingForm}
        onMaterialChange={handleMaterialChange}
        onMaterialFileChange={setMaterialFile}
        onMsdsTrainingChange={handleMsdsTrainingChange}
      />
      <ConfirmDialog open={deleteConfirmOpen} title="교육 일정 삭제" message={`‘${form.title}’ 일정과 연결 자료를 삭제할까요?`}
        pending={deleteMutation.isPending} confirmLabel="삭제" onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={() => { if (editingId && !deleteMutation.isPending) deleteMutation.mutate([editingId]); }} />
      <AutoPilotGuide
        open={autopilotOpen}
        steps={EDUCATION_AUTOPILOT_STEPS}
        stepIndex={autopilotStepIndex}
        onStepChange={setAutopilotStepIndex}
        onClose={() => setAutopilotOpen(false)}
      />
    </Box>
  );
}
